import { GetStaticProps } from 'next';
import { injectLayoutData, LFPage } from '@utils/layout';
import { REGISTER_STEPS_TYPES } from '@monofront/business/stateMachines/register';
import { PrismicDocument } from '@prismicio/client';
import { prismicService } from '@services/businessContainer';
import { sift } from 'radash';
import { RegisterFlowProps } from '@screens/RegisterFlow/RegisterFlowPage';
import dynamic from 'next/dynamic';

const NoSSRFlowComponent = dynamic(
  async () => (await import('@screens/RegisterFlow')).RegisterFlowPage,
  { ssr: false },
);

const RegisterFlowPage: LFPage<RegisterFlowProps> = (props) => (
  <NoSSRFlowComponent {...props} />
);

const fetchLinksMap: Record<string, string[]> = {
  shopaccountregister: ['giftitembanner.body'],
  shopaccountregisterv2: ['giftitembanner.body'],
  funnel: ['redirection_rules.body', 'giftitembanner.body'],
  subscription_payment: ['giftitembanner.body'],
};

RegisterFlowPage.getLayout =
  () =>
  ({ children }) =>
    children;

const pathnames: Omit<
  Record<REGISTER_STEPS_TYPES, string>,
  REGISTER_STEPS_TYPES.CONTENT
> = {
  [REGISTER_STEPS_TYPES.REGISTER]: '/account/register',
  [REGISTER_STEPS_TYPES.SUBSCRIPTION_SUMMARY]: '/account/subscription-summary',
  [REGISTER_STEPS_TYPES.SUBSCRIPTION_PAYMENT]: '/pages/subscription-payment',
};

const populateStep = async (step, previewData, locale) => {
  const linkedDocument = step?.data?.step;

  if (!linkedDocument?.uid || !linkedDocument?.type) return null;

  const fetchLinks = fetchLinksMap[linkedDocument.type] || [];

  const linkedDocumentData = await prismicService.getDocumentByUid(
    linkedDocument.type,
    linkedDocument.uid,
    previewData,
    {
      fetchLinks,
      ...(locale ? { lang: `${locale}-${locale}` } : {}),
    },
  );

  return {
    type: step.data.type,
    uid: step.data.uid,
    layout: step.data.layout || 'none',
    stepName: step.data.step_name,
    isStepperDisplayed: step.data.is_stepper_displayed,
    step: linkedDocumentData,
    trackingData: {
      ...(pathnames[step.data.type] && {
        page_path: pathnames[step.data.type],
      }),
    },
  };
};

export const getStaticProps = injectLayoutData<
  RegisterFlowProps,
  GetStaticProps<RegisterFlowProps>
>(async ({ previewData, locale }) => {
  const document: PrismicDocument = await prismicService.getDocumentByUid(
    'register_flow',
    'register-flow',
    previewData,
    {
      fetchLinks: [
        'register_flow_step.type',
        'register_flow_step.uid',
        'register_flow_step.step',
        'register_flow_step.layout',
        'register_flow_step.step_name',
        'register_flow_step.is_stepper_displayed',
      ],
      ...(locale ? { lang: `${locale}-${locale}` } : {}),
    },
  );

  if (!document) {
    return {
      notFound: true,
    };
  }

  const steps = await Promise.all(
    document.data.steps.map((data) =>
      populateStep(data.step, previewData, locale),
    ),
  );
  const abtestSteps = document.data.abtest_steps?.length
    ? await Promise.all(
        document.data.abtest_steps.map((data) =>
          populateStep(data.abtest_step, previewData, locale),
        ),
      )
    : [];

  return {
    props: {
      steps: sift(steps),
      abtestSteps: sift(abtestSteps),
    },
    revalidate: 1800, // 30min
  };
});

export default RegisterFlowPage;
